import { CookieKey } from '@/constants';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import type { ConnectFailedReason } from '../components/ESConnectFailedDialog';

let timer: NodeJS.Timeout | null = null;
const SYNC_PROGRESS_DURATION = 3 * 60 * 1000; // 3 minute in milliseconds

const useSyncingAccount = () => {
  const [connectEsFailedReason, setConnectEsFailedReason] = useState<ConnectFailedReason | null>(null);
  const [syncingAccount, setSyncingAccount] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const calculateRemainingTime = useCallback(async (expiredTime: number) => {
    const remainingTime = expiredTime ? expiredTime - new Date().getTime() : 0;
    setRemainingTime(remainingTime);
  }, []);

  useEffect(() => {
    const initRemainingTime = async () => {
      const savedExpiredTime = Cookies.get(CookieKey.SyncingESBannerExpiredTime);
      const expiredTime = savedExpiredTime ? Number.parseInt(savedExpiredTime) : 0;
      calculateRemainingTime(expiredTime);
    };

    initRemainingTime();
  }, [calculateRemainingTime]);

  useEffect(() => {
    if (!syncingAccount) return;

    const updateRemainingTime = async () => {
      const expiredTime = new Date().getTime() + SYNC_PROGRESS_DURATION;
      await Cookies.set(CookieKey.SyncingESBannerExpiredTime, expiredTime.toString());
      calculateRemainingTime(expiredTime);
    };

    updateRemainingTime();
  }, [calculateRemainingTime, syncingAccount]);

  const reset = useCallback(async () => {
    setIsSyncing(false);
    setSyncingAccount(null);
    if (timer) clearTimeout(timer);
    await Cookies.remove(CookieKey.SyncingESBannerExpiredTime);
  }, []);

  useEffect(() => {
    if (remainingTime <= 0) {
      return;
    }

    setIsSyncing(true);
    timer = setTimeout(reset, remainingTime);

    return () => {
      timer && clearTimeout(timer);
    };
  }, [remainingTime, reset]);

  return {
    isSyncing: isSyncing && remainingTime > 0,
    onDismissSyncing: reset,
    syncingAccount,
    setSyncingAccount,
    connectEsFailedReason,
    setConnectEsFailedReason,
  };
};

export default useSyncingAccount;
